/**
 * DelegateFromDocumentModal Component
 * GAP-032: Delegation entry from document view
 *
 * Allows users to quickly set up delegation access for a specific document or category.
 * Pre-selects the document's category and provides a streamlined flow.
 *
 * @requirement BRD-003 Delegation & Emergency Access
 * @gap GAP-032 No delegation entry from document view
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Button,
  Icon,
  Badge,
} from '@myvault/shared/components';
import type { Document } from '@myvault/shared';

interface DelegateFromDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
}

type DelegationOption = 'this_category' | 'all_documents' | 'full_wizard';

export function DelegateFromDocumentModal({
  isOpen,
  onClose,
  document,
}: DelegateFromDocumentModalProps) {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<DelegationOption>('this_category');

  if (!document) return null;

  const handleContinue = () => {
    // Navigate to delegation wizard with pre-filled category
    const params = new URLSearchParams();

    if (selectedOption === 'this_category' && document.category) {
      params.set('category', document.category);
      params.set('source', 'document');
      params.set('documentId', document.id);
    } else if (selectedOption === 'all_documents') {
      params.set('scope', 'all');
      params.set('source', 'document');
    }

    navigate(`/delegation/new?${params.toString()}`);
    onClose();
  };

  const options: Array<{
    value: DelegationOption;
    title: string;
    description: string;
    icon: string;
    badge?: string;
  }> = [
    {
      value: 'this_category',
      title: `Toegang tot "${document.category || 'Ongecategoriseerd'}"`,
      description: `Geef iemand toegang tot alle documenten in de categorie "${document.category || 'Ongecategoriseerd'}" inclusief dit document.`,
      icon: 'folder',
      badge: document.category ? undefined : 'Geen categorie',
    },
    {
      value: 'all_documents',
      title: 'Toegang tot alle documenten',
      description: 'Geef iemand volledige toegang tot al je documenten.',
      icon: 'documentDuplicate',
    },
    {
      value: 'full_wizard',
      title: 'Geavanceerde instellingen',
      description: 'Open de volledige wizard om specifieke categorieën, rechten en beperkingen in te stellen.',
      icon: 'adjustmentsHorizontal',
    },
  ];

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Toegang delegeren"
    >
      <div className="space-y-6">
        {/* Document Context */}
        <div className="flex items-start gap-3 p-3 bg-mv-gray-50 rounded-lg">
          <Icon name="documentText" className="w-8 h-8 text-mv-gray-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-mv-gray-900 truncate">{document.title}</p>
            <div className="flex items-center gap-2 mt-1">
              {document.category && (
                <Badge variant="default" className="text-xs">
                  {document.category}
                </Badge>
              )}
              <span className="text-xs text-mv-gray-500">
                {new Date(document.created_at).toLocaleDateString('nl-NL')}
              </span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="text-sm text-mv-gray-600">
          <p>
            Wil je iemand toegang geven tot dit document? Kies hieronder hoe je de toegang wilt instellen.
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSelectedOption(option.value)}
              className={`
                w-full text-left p-4 rounded-lg border-2 transition-all
                ${selectedOption === option.value
                  ? 'border-mv-blue-500 bg-mv-blue-50 ring-2 ring-mv-blue-200'
                  : 'border-mv-gray-200 hover:border-mv-gray-300 hover:bg-mv-gray-50'
                }
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  p-2 rounded-lg flex-shrink-0
                  ${selectedOption === option.value ? 'bg-mv-blue-100' : 'bg-mv-gray-100'}
                `}>
                  <Icon
                    name={option.icon as 'folder'}
                    className={`w-5 h-5 ${selectedOption === option.value ? 'text-mv-blue-600' : 'text-mv-gray-500'}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${selectedOption === option.value ? 'text-mv-blue-900' : 'text-mv-gray-900'}`}>
                      {option.title}
                    </span>
                    {option.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {option.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-mv-gray-600 mt-1">{option.description}</p>
                </div>
                {selectedOption === option.value && (
                  <Icon name="checkCircle" className="w-5 h-5 text-mv-blue-600 flex-shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Info Banner */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <Icon name="informationCircle" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Beveiligde toegang</p>
            <p className="mt-1">
              Je kunt altijd de toegang intrekken of aanpassen. De gemachtigde ontvangt een uitnodiging per e-mail.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-mv-gray-200">
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button variant="primary" onClick={handleContinue}>
            <Icon name="userPlus" className="w-4 h-4 mr-2" />
            Doorgaan
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DelegateFromDocumentModal;
